import {
  useChatCurrentGptModel,
  useIsChatEmpty,
  useStoredTargetAudienceId,
} from 'features/aiWriter/AiWriterSidebar/steps/chat/chatStore';
import { useResetConversationMutation } from 'features/aiWriter/AiWriterSidebar/steps/chat/useResetConversationMutation';
import { useWarnAboutChatReset } from 'features/aiWriter/AiWriterSidebar/steps/chat/useShowChatResetModal';
import { useUpdateDocumentBrandVoice } from 'features/aiWriter/AiWriterSidebar/steps/chat/useUpdateDocumentBrandVoice';
import { useGetProjectOrPreselectedLanguageAndCountry } from 'features/aiWriter/hooks/useGetProjectOrPreselectedLanguageAndCountry';
import { getActiveTab } from 'features/aiWriter/store/selectors';
import { ExtendedChatInputBrandVoiceSelect } from 'features/brand-voice/ExtendedChatInputBrandVoiceSelect';
import { useAppSelector } from 'store/hooks';

export const ProjectBrandVoiceSelector = ({ gtmId }: { gtmId?: string }) => {
  const { language, country } = useGetProjectOrPreselectedLanguageAndCountry();
  const isChatEmpty = useIsChatEmpty();
  const currentGptModel = useChatCurrentGptModel();
  const targetAudienceId = useStoredTargetAudienceId();
  const currentBrandVoiceId = useAppSelector(getActiveTab).generateTextConfig.brandVoiceId;

  const { mutateAsync: resetConversation } = useResetConversationMutation();
  const updateDocumentBrandVoice = useUpdateDocumentBrandVoice();
  const warnAboutChatReset = useWarnAboutChatReset();

  const handleBrandVoiceChange = async (brandVoiceId: string | null) => {
    if (brandVoiceId === 'create') {
      return false;
    }

    const resetAndUpdate = async () => {
      await resetConversation({
        brandVoiceId,
        gptModel: currentGptModel ?? null,
        targetAudienceId,
      });
      await updateDocumentBrandVoice({ brandVoiceId });
    };

    if (isChatEmpty) {
      await resetAndUpdate();
      return true;
    }

    return new Promise<boolean>(resolve => {
      warnAboutChatReset({
        onAccept: () => {
          resetAndUpdate().then(() => resolve(true));
        },
        onCancel: () => resolve(false),
      });
    });
  };

  return (
    <ExtendedChatInputBrandVoiceSelect
      gtmId={gtmId}
      selectedBrandVoiceId={currentBrandVoiceId ?? undefined}
      targetLanguage={language}
      targetCountry={country}
      menuOpeningDirection="top"
      onBrandVoiceChange={handleBrandVoiceChange}
    />
  );
};
